import { doc } from 'firebase/firestore';
import { safeOnSnapshot } from './firestoreSubscriptions';
import { httpsCallable } from 'firebase/functions';
import { getBytes, getMetadata, ref, uploadBytes, uploadBytesResumable } from 'firebase/storage';
import { db, functions, storage } from '../firebase-client';

const MEDIA_UPLOAD_TIMEOUT_MS = 300000;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_VIDEO_BYTES = 250 * 1024 * 1024;
export const MAX_MEDIA_ATTACHMENTS = 10;
export const MAX_VIDEO_ATTACHMENTS = 1;
const VIDEO_FRAME_COUNT = 5;
const VIDEO_FRAME_MAX_DIMENSION = 1280;
const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const VIDEO_TYPES = new Set(['video/mp4', 'video/quicktime', 'video/webm']);

function formatBytes(bytes = 0) {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return '0 MB';
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(value >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
  return `${Math.max(1, Math.round(value / 1024))} KB`;
}

function mediaKindForFile(file) {
  const type = String(file?.type || '').toLowerCase();
  if (IMAGE_TYPES.has(type)) return 'image';
  if (VIDEO_TYPES.has(type)) return 'video';
  return '';
}

export function validateMediaAttachmentFiles(files = [], existingMedia = []) {
  const nextFiles = Array.from(files || []).filter(Boolean);
  const currentMedia = Array.isArray(existingMedia) ? existingMedia.filter(Boolean) : [];
  if (currentMedia.length + nextFiles.length > MAX_MEDIA_ATTACHMENTS) {
    throw new Error(`You can attach up to ${MAX_MEDIA_ATTACHMENTS} media files.`);
  }

  const currentVideoCount = currentMedia.filter((media) => media?.mediaType === 'video').length;
  const nextVideoCount = nextFiles.filter((file) => mediaKindForFile(file) === 'video').length;
  if (currentVideoCount + nextVideoCount > MAX_VIDEO_ATTACHMENTS) {
    throw new Error('You can attach one video per message.');
  }
}

function assertClientMediaAllowed(file) {
  const mediaType = mediaKindForFile(file);
  if (!mediaType) {
    throw new Error('Choose a JPEG, PNG, WebP, MP4, MOV, or WebM file.');
  }
  const maxBytes = mediaType === 'video' ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
  if (Number(file?.size || 0) > maxBytes) {
    const label = mediaType === 'video' ? 'Videos' : 'Images';
    throw new Error(`${label} must be ${formatBytes(maxBytes)} or smaller. This file is ${formatBytes(file.size)}.`);
  }
}

function waitForVideoEvent(video, eventName, timeoutMs = 12000) {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => cleanup(reject, new Error('Video frame preparation timed out.')), timeoutMs);
    const cleanup = (done, value) => {
      window.clearTimeout(timeoutId);
      video.removeEventListener(eventName, handleEvent);
      video.removeEventListener('error', handleError);
      done(value);
    };
    const handleEvent = () => cleanup(resolve);
    const handleError = () => cleanup(reject, new Error('Video frame preparation failed.'));
    video.addEventListener(eventName, handleEvent, { once: true });
    video.addEventListener('error', handleError, { once: true });
  });
}

function seekVideo(video, time) {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => cleanup(reject, new Error('Video frame seek timed out.')), 10000);
    const cleanup = (done, value) => {
      window.clearTimeout(timeoutId);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('error', handleError);
      done(value);
    };
    const handleSeeked = () => cleanup(resolve);
    const handleError = () => cleanup(reject, new Error('Video frame seek failed.'));
    video.addEventListener('seeked', handleSeeked, { once: true });
    video.addEventListener('error', handleError, { once: true });
    video.currentTime = time;
  });
}

function canvasToJpegBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Video frame capture failed.'));
    }, 'image/jpeg', 0.82);
  });
}

async function extractVideoFrameBlobs(file) {
  if (typeof document === 'undefined' || typeof window === 'undefined') return [];

  const objectUrl = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.muted = true;
  video.playsInline = true;
  video.src = objectUrl;

  try {
    await waitForVideoEvent(video, 'loadedmetadata');
    const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 1;
    const scanWindow = Math.max(0.2, Math.min(duration, 60));
    const frameCount = Math.min(VIDEO_FRAME_COUNT, Math.max(1, Math.ceil(scanWindow / 12)));
    const times = Array.from({ length: frameCount }, (_, index) => {
      if (frameCount === 1) return Math.min(0.1, Math.max(0, duration - 0.05));
      const offset = index / (frameCount - 1);
      return Math.min(duration - 0.05, 0.1 + (Math.max(0, scanWindow - 0.2) * offset));
    }).map((time) => Math.max(0, time));

    const width = video.videoWidth || 0;
    const height = video.videoHeight || 0;
    if (!width || !height) return [];
    const scale = Math.min(1, VIDEO_FRAME_MAX_DIMENSION / Math.max(width, height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const context = canvas.getContext('2d');
    if (!context) return [];

    const frames = [];
    for (const time of times) {
      await seekVideo(video, time);
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      frames.push(await canvasToJpegBlob(canvas));
    }
    return frames;
  } finally {
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(objectUrl);
  }
}

export function getMediaLabel(media = {}) {
  return media?.mediaType === 'video' ? 'Video' : 'Image';
}

export async function fetchApprovedMediaBlob({ uploadId, storagePath, maxBytes = MAX_IMAGE_BYTES } = {}) {
  if (!uploadId || !storagePath) throw new Error('Media access details are missing.');
  const url = await fetchApprovedMediaAccessUrl({ uploadId, storagePath });
  const response = await fetch(url);
  if (!response.ok) throw new Error('Media file is no longer available.');
  const blob = await response.blob();
  if (Number(blob.size || 0) > maxBytes) {
    throw new Error('Media file is too large to load.');
  }
  return blob;
}

export async function fetchMediaBlob(storagePath, maxBytes = MAX_IMAGE_BYTES, media = {}) {
  if (!storagePath) return '';
  const uploadId = typeof media === 'string' ? media : media?.uploadId;
  if (uploadId) {
    return fetchApprovedMediaBlob({ uploadId, storagePath, maxBytes });
  }
  const fileRef = ref(storage, storagePath);
  const [metadata, bytes] = await Promise.all([
    getMetadata(fileRef),
    getBytes(fileRef, maxBytes),
  ]);
  return new Blob([bytes], {
    type: metadata.contentType || 'application/octet-stream',
  });
}

export async function fetchMediaObjectUrl(storagePath, maxBytes = MAX_IMAGE_BYTES, media = {}) {
  const blob = await fetchMediaBlob(storagePath, maxBytes, media);
  return URL.createObjectURL(blob);
}

export async function fetchVideoObjectUrl(storagePath, media = {}) {
  return fetchMediaObjectUrl(storagePath, MAX_VIDEO_BYTES, media);
}

export async function fetchApprovedMediaAccessUrl(media = {}) {
  if (!media?.uploadId || !media?.storagePath) return '';
  const getAccessUrl = httpsCallable(functions, 'getApprovedMediaAccessUrl');
  const result = await getAccessUrl({
    uploadId: media.uploadId,
    storagePath: media.storagePath,
  });
  return result.data?.url || '';
}

export async function discardMediaUpload(uploadId) {
  if (!uploadId) return null;
  const discard = httpsCallable(functions, 'discardMediaUpload');
  const result = await discard({ uploadId });
  return result.data;
}

function waitForUploadScan(uploadId, onStateChange) {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      unsubscribe();
      reject(new Error('Media upload timed out.'));
    }, MEDIA_UPLOAD_TIMEOUT_MS);

    const unsubscribe = safeOnSnapshot(doc(db, 'mediaUploads', uploadId), (snap) => {
      if (!snap.exists()) return;
      const data = { id: snap.id, ...snap.data() };
      const reportedStatus = data.status === 'awaiting_upload' ? 'scanning' : data.status;
      onStateChange?.({ status: reportedStatus, upload: data });
      if (data.status === 'approved') {
        window.clearTimeout(timeoutId);
        unsubscribe();
        resolve(data);
      } else if (['blocked', 'error', 'deleted'].includes(data.status)) {
        window.clearTimeout(timeoutId);
        unsubscribe();
        reject(new Error(data.status === 'blocked'
          ? 'This upload may violate our terms of service, it is being reviewed by platform staff.'
          : 'Media could not be processed.'));
      }
    }, (error) => {
      window.clearTimeout(timeoutId);
      unsubscribe();
      reject(error);
    });
  });
}

export async function uploadModeratedMedia({
  file,
  surface,
  target = {},
  onProgress,
  onStateChange,
}) {
  if (!file) throw new Error('Choose a file to upload.');
  assertClientMediaAllowed(file);

  const prepareMediaUpload = httpsCallable(functions, 'prepareMediaUpload');
  onStateChange?.({ status: 'preparing' });
  const prepared = await prepareMediaUpload({
    surface,
    contentType: file.type,
    size: file.size,
    fileName: file.name,
    ...target,
  });
  const { uploadId, quarantinePath } = prepared.data || {};
  if (!uploadId || !quarantinePath) throw new Error('Could not prepare media upload.');

  const mediaType = mediaKindForFile(file);
  const frameBlobs = mediaType === 'video'
    ? await extractVideoFrameBlobs(file).catch(() => [])
    : [];
  if (frameBlobs.length > 0) {
    const quarantineBasePath = quarantinePath.replace(/\/original$/, '');
    for (let index = 0; index < frameBlobs.length; index += 1) {
      const frameIndex = String(index + 1).padStart(3, '0');
      await uploadBytes(ref(storage, `${quarantineBasePath}/frames/frame-${frameIndex}.jpg`), frameBlobs[index], {
        contentType: 'image/jpeg',
        customMetadata: { uploadId, frameIndex },
      });
    }
  }

  onStateChange?.({ status: 'uploading', uploadId });
  const task = uploadBytesResumable(ref(storage, quarantinePath), file, {
    contentType: file.type,
    customMetadata: { uploadId },
  });

  await new Promise((resolve, reject) => {
    task.on('state_changed', (snapshot) => {
      const progress = snapshot.totalBytes
        ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
        : 0;
      onProgress?.(progress);
    }, reject, resolve);
  });

  onStateChange?.({ status: 'scanning', uploadId });
  const upload = await waitForUploadScan(uploadId, onStateChange);
  return {
    provider: 'firebase_upload',
    uploadId,
    mediaType: upload.mediaType,
    contentType: upload.contentType,
    storagePath: upload.approvedPath,
    thumbnailPath: upload.thumbnailPath || null,
    size: upload.size,
    fileName: upload.fileName || file.name,
  };
}
